// @danielx/song-structure — where the sections of a song are, which ones
// repeat, and which channel carries the tune.
//
// Extracted from chip/source/arranger/analyze.mjs so Paint Composer's
// practice auto-config and chip's arrange lab run the SAME structural
// analysis. Everything here is pure data-in data-out on a row grid: callers
// bring notes as {startTick, endTick, pitch} (any tick unit; rowTicks says
// how many make a row), and get back unit boundaries, section labels, and
// role assignments. No MIDI reading, no rendering, no DOM.

// Bars are still the unit of MEANING (the UI talks in bars, the phrase grid is
// 4 of them); half-bars are the unit of RESOLUTION, because a pickup into the
// chorus and a half-bar break are real structure a bar grid cannot say.
export const PHRASE_BARS = 4
export const MAX_SECTION_BARS = 16
// A section shorter than this must EARN its boundaries with evidence well
// above the noise floor — the floor exists to stop arbitrary fragments, not
// interludes.
export const MIN_EARNED_BARS = 4

// ── per-unit features ─────────────────────────────────────────────────────────
// Which SOURCE voices sound in a unit matters far more than pitch or density:
// arrangements announce their sections by adding and dropping instruments.
// Presence is measured by SOUNDING (a whole note held across a unit is still
// there), density by onsets. The weights keep the voice set dominant, matching
// what the old channel-Jaccard distance measured on a 60-song sample.
export const W_PRESENCE = 1.5
export const W_DENSITY = 1.0
export const W_DRUM_PRESENCE = 0.9
export const W_DRUM_DENSITY = 0.5
export const W_PITCH = 0.7

export const unitFeatures = (melodic, drums, rowTicks, unitRows, totalUnits) => {
  const keys = [...melodic.keys()]
  const D = keys.length + 4
  const raw = Array.from({ length: totalUnits }, () => ({
    present: new Float64Array(keys.length), onsets: 0, drums: 0, pitchSum: 0, pitchN: 0,
  }))
  const unitOf = (row) => Math.floor(row / unitRows)
  keys.forEach((key, k) => {
    for (const n of melodic.get(key)) {
      const from = unitOf(Math.round(n.startTick / rowTicks))
      const to = unitOf(Math.max(Math.round(n.startTick / rowTicks), Math.round(n.endTick / rowTicks) - 1))
      for (let u = from; u <= Math.min(to, totalUnits - 1); u++) if (u >= 0) raw[u].present[k] = 1
      const at = raw[from]
      if (at) { at.onsets++; at.pitchSum += n.pitch; at.pitchN++ }
    }
  })
  for (const n of drums) {
    const at = raw[unitOf(Math.round(n.startTick / rowTicks))]
    if (at) at.drums++
  }
  return raw.map((r) => {
    const x = new Float64Array(D)
    for (let k = 0; k < keys.length; k++) x[k] = W_PRESENCE * r.present[k]
    x[keys.length] = W_DENSITY * Math.min(1, r.onsets / unitRows)
    x[keys.length + 1] = W_DRUM_PRESENCE * (r.drums ? 1 : 0)
    x[keys.length + 2] = W_DRUM_DENSITY * Math.min(1, r.drums / unitRows)
    x[keys.length + 3] = r.pitchN ? W_PITCH * Math.max(-1.5, Math.min(1.5, (r.pitchSum / r.pitchN - 60) / 24)) : 0
    return x
  })
}

// ── the segmentation itself ───────────────────────────────────────────────────
// Sum of squared deviations from the segment mean, in O(1) per query via
// prefix sums. This is the "a section is a homogeneous block" idea stated as a
// cost instead of approximated by window heuristics.
export const prefixSums = (X) => {
  const U = X.length, D = X[0]?.length ?? 0
  const S = [new Float64Array(D)], Q = [new Float64Array(D)]
  for (let u = 0; u < U; u++) {
    const s = Float64Array.from(S[u]), q = Float64Array.from(Q[u])
    for (let d = 0; d < D; d++) { s[d] += X[u][d]; q[d] += X[u][d] * X[u][d] }
    S.push(s); Q.push(q)
  }
  return { S, Q, U, D }
}
export const ssdOf = ({ S, Q, D }) => (i, j) => {
  const n = j - i
  if (n <= 1) return 0
  let total = 0
  for (let d = 0; d < D; d++) {
    const s = S[j][d] - S[i][d]
    total += Q[j][d] - Q[i][d] - (s * s) / n
  }
  return Math.max(0, total)
}

// The boundary penalty is scaled by the song's OWN noise floor — the median
// per-unit variance of short windows — so "how much evidence buys a cut" means
// the same thing on a sparse ballad and a dense band chart. The absolute floor
// keeps a near-uniform song from being cut on nothing.
export const noiseFloor = (ssd, U) => {
  const w = 8
  const locals = []
  for (let i = 0; i + w <= U; i += 4) locals.push(ssd(i, i + w) / w)
  locals.sort((a, b) => a - b)
  return locals[locals.length >> 1] ?? 0
}

// Swept against the same fixtures the old constants were: lower LAMBDA_SCALE
// over-cuts ordinary variation, higher misses the blocked edges.
export const LAMBDA_SCALE = 3.0
export const LAMBDA_FLOOR = 0.4
// Most structure lands on the phrase grid; a barline cut needs to be better
// than an on-grid one, and a mid-bar cut (pickup, break) better still.
export const gridPenalty = (bars) =>
  bars % PHRASE_BARS === 0 ? 1 : Number.isInteger(bars) ? 1.35 : 1.8

export const segmentUnits = (X, { maxUnits, barsPerUnit }) => {
  const U = X.length
  if (U <= 1) return [0]
  const ssd = ssdOf(prefixSums(X))
  const lambda = LAMBDA_SCALE * noiseFloor(ssd, U) + LAMBDA_FLOOR

  const cost = new Float64Array(U + 1).fill(Infinity)
  const prev = new Int32Array(U + 1)
  cost[0] = 0
  for (let j = 1; j <= U; j++) {
    for (let i = Math.max(0, j - maxUnits); i < j; i++) {
      if (cost[i] === Infinity) continue
      const c = cost[i] + ssd(i, j) + (i > 0 ? lambda * gridPenalty(i * barsPerUnit) : 0)
      if (c < cost[j]) { cost[j] = c; prev[j] = i }
    }
  }
  const bounds = []
  for (let j = U; j > 0; j = prev[j]) bounds.push(prev[j])
  bounds.reverse()

  // A short section must EARN its edges: for each segment under the floor,
  // measure what merging it away would cost in homogeneity. Real breaks and
  // pickups are unlike their surroundings and pay easily; a leftover from the
  // length cap is not and does not. When the merge would breach the cap, the
  // boundary is RELOCATED to the phrase-grid position nearest the middle of
  // the joined span instead of dropped — that is the proportional filler
  // split, produced by the same rule instead of a second pass.
  const minUnits = Math.round(MIN_EARNED_BARS / barsPerUnit)
  // Swept on a 52-song corpus slice: 2.0 leaves a third of all sections
  // under 4 bars (ordinary churn qualifying), 4.0 lands at ~11% while real
  // breaks (a 1-bar stop recurring at the same spot in every verse cycle)
  // still earn their keep; 5.0 barely differs.
  const earn = 4.0 * lambda
  const phraseUnits = Math.round(PHRASE_BARS / barsPerUnit)
  for (let pass = 0; pass < bounds.length + 8; pass++) {
    let changed = false
    for (let s = 0; s < bounds.length && !changed; s++) {
      const from = bounds[s]
      const to = bounds[s + 1] ?? U
      if (to - from >= minUnits) continue
      // The gain each edge contributes: joined-cost minus split-cost.
      const edges = []
      if (s > 0) edges.push({ at: s, gain: ssd(bounds[s - 1], to) - ssd(bounds[s - 1], from) - ssd(from, to) })
      if (s + 1 < bounds.length) {
        const right = bounds[s + 2] ?? U
        edges.push({ at: s + 1, gain: ssd(from, right) - ssd(from, to) - ssd(to, right) })
      }
      if (!edges.length) break
      const weakest = edges.reduce((a, b) => (a.gain <= b.gain ? a : b))
      if (weakest.gain >= earn) continue
      const leftIndex = weakest.at - 1
      const left = bounds[leftIndex]
      const right = bounds[weakest.at + 1] ?? U
      if (right - left <= maxUnits) {
        bounds.splice(weakest.at, 1)
      } else {
        const mid = left + Math.round((right - left) / 2 / phraseUnits) * phraseUnits
        const moved = Math.max(left + 1, Math.min(right - 1, mid))
        if (moved === bounds[weakest.at]) continue
        bounds[weakest.at] = moved
      }
      changed = true
    }
    if (!changed) break
  }

  // A SUB-BAR segment is a pickup or a break, and either way it BELONGS to
  // the section it introduces: the half-bar lead-in is the first thing the
  // chorus says, and the half-bar stop is the breath before it. Keeping it as
  // a section of its own would triple the section count of any song with a
  // recurring pickup (measured: a verse cycle became six sections instead of
  // four). So the cut at its START survives — that edge is real — and the cut
  // at its end dissolves into the section that follows. The join can overrun
  // the length cap by the sub-bar itself, which is the right tradeoff: the
  // cap bounds decisions, and a pickup is not a decision.
  if (barsPerUnit < 1) {
    for (let s = bounds.length - 1; s >= 0; s--) {
      const from = bounds[s]
      const to = bounds[s + 1] ?? U
      if ((to - from) * barsPerUnit >= 1) continue
      if (s + 1 < bounds.length) bounds.splice(s + 1, 1)       // merge into what follows
      else if (s > 0) bounds.splice(s, 1)                      // a sub-bar tail joins what came before
    }
  }
  return bounds
}

// ── section identity ──────────────────────────────────────────────────────────
// So a repeated chorus reads as the SAME section in the UI and one decision
// can cover all of its instances. The signature is a bag of (row offset,
// pitch class) over EVERY melodic voice — pitch class, because a chorus
// lifted an octave for the last go round is the same chorus; every voice
// rather than the three folded roles, so a role swap cannot relabel the song.
export const signatureOf = (voices, startRow, endRow) => {
  const bag = new Set()
  for (const v of voices) {
    for (const n of v.raw) {
      if (n.row < startRow || n.row >= endRow) continue
      bag.add(`${n.row - startRow}:${n.pitch % 12}`)
    }
  }
  return bag
}

// Jaccard: shared over union. Symmetric, length-aware (a section twice as long
// cannot match a short one just by containing it), and cheap. 0.45 swept on a
// 60-song sample: recognises a repeat in 42 of 60 songs while never collapsing
// a song to a single label.
export const SAME_SECTION = 0.45
export const similarity = (a, b) => {
  if (!a.size && !b.size) return 1
  if (!a.size || !b.size) return 0
  let shared = 0
  for (const key of a) if (b.has(key)) shared++
  return shared / (a.size + b.size - shared)
}

export const labelSections = (signatures) => {
  const labels = []
  const groups = []
  for (const sig of signatures) {
    let best = null
    for (const g of groups) {
      const score = similarity(sig, g.signature)
      if (score >= SAME_SECTION && (!best || score > best.score)) best = { score, label: g.label }
    }
    if (best) {
      labels.push(best.label)
    } else {
      const label = letterFor(groups.length)
      groups.push({ signature: sig, label })
      labels.push(label)
    }
  }
  return labels
}

export const letterFor = (index) => {
  let s = ''
  do { s = String.fromCharCode(65 + (index % 26)) + s; index = Math.floor(index / 26) - 1 } while (index >= 0)
  return s
}

// ── role assignment ───────────────────────────────────────────────────────────
export const channelStats = (notes, rowsPerBar, rowTicks) => {
  let dur = 0, pitchDur = 0
  const bars = new Set()
  const rows = new Set()
  for (const n of notes) {
    const d = Math.max(1, n.endTick - n.startTick)
    dur += d
    pitchDur += n.pitch * d
    const row = Math.round(n.startTick / rowTicks)
    rows.add(row)
    bars.add(Math.floor(row / rowsPerBar))
  }
  return { meanPitch: pitchDur / dur, onsets: rows.size, bars }
}

// Pick melody / harmony / bass from the melodic MIDI channels. Coverage (how
// much of the song a channel is present for) dominates: an 8-bar flute solo
// must not win melody over the lead that carries the piece.
export const assignRoles = (byChannel, totalBars, rowsPerBar, rowTicks) => {
  const stats = new Map()
  for (const [ch, notes] of byChannel) {
    if (notes.length < 4) continue
    stats.set(ch, channelStats(notes, rowsPerBar, rowTicks))
  }
  const coverage = (ch) => stats.get(ch).bars.size / (totalBars || 1)
  const candidates = [...stats.keys()]
  if (!candidates.length) return { melody: null, harmony: null, bass: null, stats }

  const melodyScore = (ch) => {
    const s = stats.get(ch)
    return 1.5 * coverage(ch) + s.meanPitch / 127 + 0.5 * Math.min(s.onsets / (totalBars * 4 || 1), 1)
  }
  const melody = candidates.reduce((a, b) => (melodyScore(a) >= melodyScore(b) ? a : b))
  const rest = candidates.filter((c) => c !== melody && coverage(c) >= 0.15)

  let bass = null
  for (const ch of rest) {
    if (stats.get(ch).meanPitch <= stats.get(melody).meanPitch - 5 &&
        (bass === null || stats.get(ch).meanPitch < stats.get(bass).meanPitch)) bass = ch
  }
  let harmony = null
  for (const ch of rest) {
    if (ch === bass) continue
    if (harmony === null || coverage(ch) > coverage(harmony)) harmony = ch
  }
  return { melody, harmony, bass, stats }
}
